/**
 * Box / cube UV projection for export baking — port of Sean's
 * `buildProjectedUV.js` prototype (TraceFrontend, `sean/uvprojector-prototype`).
 *
 * At export we bake morph-stretched geometry to static GLBs. Runtime triplanar
 * shading is not expressible in glTF, so the baked mesh needs NEW UVs that
 * RE-TILE (not stretch) directional textures. Algorithm, per the prototype:
 *
 *   1. weld coincident verts (topology only — the output stays unindexed)
 *   2. label every triangle with a projection direction: argmax of sg·N per
 *      axis over 6 signed directions (±X ±Y ±Z)
 *   3. optional ICM seam routing: iteratively relabel triangles to minimize
 *      dataCost + LAMBDA·edgeLen·dot^CREASE_POWER across shared edges, which
 *      pushes label seams onto creases where the eye expects discontinuity
 *   4. planar UV write per label; U is flipped on negative
 *      sides so textures and normal maps don't mirror
 *   5. stretch metric: singular values of the 3D→UV mapping per triangle
 *
 * Dropped from the prototype (debug/experimental surface): best-fit chart
 * planes, the `basis` reorientation option, axis/stretch debug color
 * attributes, and seam position line lists. The numeric seam/stretch stats
 * are kept.
 *
 * Multi-material caveat: this module is strictly per-geometry. The prototype's
 * multi-material behavior is UNVERIFIED (Sean, Slack 2026-08-07) — if a mesh
 * has submesh/material ranges, splitting the vertex ranges and calling this
 * per range is the CALLER's concern.
 */
import type { Matrix } from "@babylonjs/core/Maths/math.vector";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

import { KEY_QUANT } from "./meshGeometryOps";

export interface UVProjectOptions {
    /** Texture repetitions per projection-space meter (= TriplanarConfig.tileSize). */
    scale: number;
    /**
     * 'routed' (default, what every export uses) = crease-aware ICM;
     * 'naive' = per-triangle argmax label — kept as the measured baseline
     * the routing quality tests compare against, not a production mode.
     */
    method?: "naive" | "routed";
    /** Max ICM sweeps (breaks early once a sweep changes nothing). Default 8. */
    iterations?: number;
}

// Seam-routing weights — prototype defaults, never varied in practice:
// LAMBDA is the cost of a label change across an edge (relative to data
// cost); CREASE_POWER is the exponent on the face-normal dot in the edge
// weight — higher makes creases cheaper to cut.
const LAMBDA = 1.0;
const CREASE_POWER = 4.0;

export interface UVProjectResult {
    /** Per-vertex UVs, unindexed (2 floats × 3 verts × triCount). */
    uv: Float32Array;
    /** Area-weighted average and max per-triangle stretch (1 = distortion-free). */
    stretchStats: { avg: number; max: number };
    /** Label-mismatch shared edges: count and total length in INPUT (pre-matrix) space. */
    seamStats: { edges: number; length: number };
}

// Labels are the 6 signed directions ±X ±Y ±Z (label = axis*2 + (negative?1:0)):
// U flips on negative sides so textures/normal maps don't mirror, and sign
// boundaries become real label seams.
function axisOf(label: number): number {
    return label >> 1;
}
function signOf(label: number): number {
    return (label & 1) ? -1 : 1;
}

/**
 * Build a projected UV set for an UNINDEXED triangle soup (3 verts per
 * triangle). `matrix` maps positions into projection space; `normalMatrix`
 * maps normals (caller supplies the inverse-transpose — only its 3×3 part is
 * used). Vertex order is preserved: `uv[i]` belongs to `positions[i]`.
 */
export function buildProjectedUV(
    positions: Float32Array,
    normals: Float32Array,
    matrix: Matrix,
    normalMatrix: Matrix,
    opts: UVProjectOptions,
): UVProjectResult {
    const {
        scale,
        method = "routed",
        iterations = 8,
    } = opts;

    if (positions.length % 9 !== 0) {
        throw new Error(`buildProjectedUV: positions must be unindexed triangles (length % 9 === 0), got ${positions.length}`);
    }
    if (normals.length !== positions.length) {
        throw new Error(`buildProjectedUV: normals length ${normals.length} != positions length ${positions.length}`);
    }

    const count = positions.length / 3;
    const triCount = count / 3;
    const dirCount = 6;

    // Projection-space positions and shading normals. Input positions double
    // as the prototype's "local" positions: weld keys and edge lengths (and
    // therefore seamStats.length) are measured in INPUT space, pre-matrix —
    // faithful to the prototype, which welded on local coordinates.
    const WP = new Float32Array(count * 3);
    const WN = new Float32Array(count * 3);
    const v = new Vector3();
    const n = new Vector3();
    for (let i = 0; i < count; i++) {
        v.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
        Vector3.TransformCoordinatesToRef(v, matrix, v);
        WP[i * 3] = v.x; WP[i * 3 + 1] = v.y; WP[i * 3 + 2] = v.z;
        n.set(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]);
        Vector3.TransformNormalToRef(n, normalMatrix, n);
        n.normalize();
        WN[i * 3] = n.x; WN[i * 3 + 1] = n.y; WN[i * 3 + 2] = n.z;
    }

    // Weld coincident verts -> ids (quantized at 1e-4 per component, matching
    // the prototype's round(x * 1e4) keying).
    const idOf = new Int32Array(count);
    const keyToId = new Map<string, number>();
    const idPos: number[] = []; // 3 floats per welded id (first occurrence wins)
    let nextId = 0;
    for (let i = 0; i < count; i++) {
        const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
        const key = Math.round(x * KEY_QUANT) + "_" + Math.round(y * KEY_QUANT) + "_" + Math.round(z * KEY_QUANT);
        let id = keyToId.get(key);
        if (id === undefined) {
            id = nextId++;
            keyToId.set(key, id);
            idPos.push(x, y, z);
        }
        idOf[i] = id;
    }

    // Per-triangle face normal, area, data cost, initial (argmax) label.
    const faceN = new Float32Array(triCount * 3);
    const faceArea = new Float32Array(triCount);
    const dataCost = new Float32Array(triCount * dirCount);
    const label = new Uint8Array(triCount);
    for (let t = 0; t < triCount; t++) {
        const a = 3 * t, b = 3 * t + 1, c = 3 * t + 2;
        const e1x = WP[b * 3] - WP[a * 3], e1y = WP[b * 3 + 1] - WP[a * 3 + 1], e1z = WP[b * 3 + 2] - WP[a * 3 + 2];
        const e2x = WP[c * 3] - WP[a * 3], e2y = WP[c * 3 + 1] - WP[a * 3 + 1], e2z = WP[c * 3 + 2] - WP[a * 3 + 2];
        let fnx = e1y * e2z - e1z * e2y;
        let fny = e1z * e2x - e1x * e2z;
        let fnz = e1x * e2y - e1y * e2x;
        const fnl = Math.hypot(fnx, fny, fnz);
        faceArea[t] = 0.5 * fnl;
        if (fnl > 0) { fnx /= fnl; fny /= fnl; fnz /= fnl; }
        faceN[t * 3] = fnx; faceN[t * 3 + 1] = fny; faceN[t * 3 + 2] = fnz;
        // Shading normal = normalized sum of the three vertex normals (a zero
        // sum stays zero, as with the prototype's guarded normalize()).
        let snx = WN[a * 3] + WN[b * 3] + WN[c * 3];
        let sny = WN[a * 3 + 1] + WN[b * 3 + 1] + WN[c * 3 + 1];
        let snz = WN[a * 3 + 2] + WN[b * 3 + 2] + WN[c * 3 + 2];
        const snl = Math.hypot(snx, sny, snz);
        if (snl > 0) { snx /= snl; sny /= snl; snz /= snl; }
        const N = [snx, sny, snz];
        let best = 0, bestCost = Infinity;
        for (let d = 0; d < dirCount; d++) {
            const ax = axisOf(d), sg = signOf(d);
            const cost = 1 - Math.max(0, sg * N[ax]);
            dataCost[t * dirCount + d] = cost;
            if (cost < bestCost) { bestCost = cost; best = d; }
        }
        label[t] = best;
    }

    // Shared-edge adjacency over welded vertex ids.
    interface Edge { tris: number[]; len: number }
    const edgeMap = new Map<string, Edge>();
    const addEdge = (u: number, w: number, t: number): void => {
        const lo = Math.min(u, w), hi = Math.max(u, w);
        const key = lo + "_" + hi;
        let e = edgeMap.get(key);
        if (!e) {
            const dx = idPos[lo * 3] - idPos[hi * 3];
            const dy = idPos[lo * 3 + 1] - idPos[hi * 3 + 1];
            const dz = idPos[lo * 3 + 2] - idPos[hi * 3 + 2];
            e = { tris: [], len: Math.hypot(dx, dy, dz) };
            edgeMap.set(key, e);
        }
        e.tris.push(t);
    };
    for (let t = 0; t < triCount; t++) {
        const a = idOf[3 * t], b = idOf[3 * t + 1], c = idOf[3 * t + 2];
        addEdge(a, b, t); addEdge(b, c, t); addEdge(c, a, t);
    }
    interface Neighbor { j: number; w: number }
    const neighbors: Neighbor[][] = Array.from({ length: triCount }, () => []);
    for (const e of edgeMap.values()) {
        // Non-manifold edges (>2 tris) carry no smoothing term, as in the prototype.
        if (e.tris.length !== 2) continue;
        const [t0, t1] = e.tris;
        const dot = faceN[t0 * 3] * faceN[t1 * 3]
            + faceN[t0 * 3 + 1] * faceN[t1 * 3 + 1]
            + faceN[t0 * 3 + 2] * faceN[t1 * 3 + 2];
        const w = e.len * Math.pow(Math.max(0, dot), CREASE_POWER);
        neighbors[t0].push({ j: t1, w });
        neighbors[t1].push({ j: t0, w });
    }

    // ICM routing: greedy per-triangle relabel sweeps until a sweep changes
    // nothing (local minimum) or `iterations` is exhausted.
    if (method === "routed") {
        for (let it = 0; it < iterations; it++) {
            let changed = 0;
            for (let t = 0; t < triCount; t++) {
                const nb = neighbors[t];
                let best = label[t], bestCost = Infinity;
                for (let L = 0; L < dirCount; L++) {
                    let cost = dataCost[t * dirCount + L];
                    for (let k = 0; k < nb.length; k++) {
                        if (label[nb[k].j] !== L) cost += LAMBDA * nb[k].w;
                    }
                    if (cost < bestCost) { bestCost = cost; best = L; }
                }
                if (best !== label[t]) { label[t] = best; changed++; }
            }
            if (changed === 0) break;
        }
    }

    // Planar UV write per label. Signed mode flips U on negative sides — this
    // is what keeps the texture (and its normal map) from mirroring.
    const uv = new Float32Array(count * 2);
    for (let t = 0; t < triCount; t++) {
        const L = label[t];
        const axis = axisOf(L), sg = signOf(L);
        for (let k = 0; k < 3; k++) {
            const i = 3 * t + k;
            const x = WP[i * 3] * scale, y = WP[i * 3 + 1] * scale, z = WP[i * 3 + 2] * scale;
            let uu: number, vv: number;
            // Positive faces must match the runtime shader exactly —
            // xProj samples pos.yz, yProj pos.xz, zProj pos.xy
            // (TriplanarMaterial triplanarSample). The prototype wrote
            // (z, y) for the X axis, which renders directional textures
            // 90°-rotated relative to our shader; deliberate deviation.
            // The un-mirroring sign flip rides on the coordinate that
            // reverses between the +/- views (horizontal on the face), so
            // for X faces it moves to V (= z) — flipping U (= y) would
            // turn the texture upside-down across the pair instead.
            if (axis === 0) { uu = y; vv = z * sg; }
            else if (axis === 1) { uu = x * sg; vv = z; }
            else { uu = x * sg; vv = y; }
            uv[i * 2] = uu; uv[i * 2 + 1] = vv;
        }
    }

    // Per-triangle stretch: singular values of the 2×2 mapping from an
    // orthonormal in-plane triangle basis to UV space, normalized by `scale`
    // so 1.0 means the texture tiles at exactly the requested density.
    let stretchAreaSum = 0, stretchWeighted = 0, stretchMax = 0;
    for (let t = 0; t < triCount; t++) {
        const a = 3 * t, b = 3 * t + 1, c = 3 * t + 2;
        let xhx = WP[b * 3] - WP[a * 3], xhy = WP[b * 3 + 1] - WP[a * 3 + 1], xhz = WP[b * 3 + 2] - WP[a * 3 + 2];
        const e1l = Math.hypot(xhx, xhy, xhz) || 1e-9;
        xhx /= e1l; xhy /= e1l; xhz /= e1l;
        const tmpx = WP[c * 3] - WP[a * 3], tmpy = WP[c * 3 + 1] - WP[a * 3 + 1], tmpz = WP[c * 3 + 2] - WP[a * 3 + 2];
        const a2x = tmpx * xhx + tmpy * xhy + tmpz * xhz;
        let yhx = tmpx - xhx * a2x, yhy = tmpy - xhy * a2x, yhz = tmpz - xhz * a2x;
        const yl = Math.hypot(yhx, yhy, yhz) || 1e-9;
        yhx /= yl; yhy /= yl; yhz /= yl;
        const a2y = tmpx * yhx + tmpy * yhy + tmpz * yhz;
        const f1u = uv[b * 2] - uv[a * 2], f1v = uv[b * 2 + 1] - uv[a * 2 + 1];
        const f2u = uv[c * 2] - uv[a * 2], f2v = uv[c * 2 + 1] - uv[a * 2 + 1];
        const detA = e1l * a2y; // det of [[e1l, 0], [a2x, a2y]]
        let smax = scale, smin = scale;
        if (Math.abs(detA) > 1e-12) {
            // Inverse of [[e1l, 0], [a2x, a2y]]; its [1][0] entry is exactly 0.
            const inv00 = a2y / detA, inv01 = -a2x / detA, inv10 = 0, inv11 = e1l / detA;
            const m00 = f1u * inv00 + f2u * inv10;
            const m01 = f1u * inv01 + f2u * inv11;
            const m10 = f1v * inv00 + f2v * inv10;
            const m11 = f1v * inv01 + f2v * inv11;
            const E = m00 * m00 + m01 * m01 + m10 * m10 + m11 * m11;
            const det = m00 * m11 - m01 * m10;
            const disc = Math.sqrt(Math.max(0, E * E - 4 * det * det));
            smax = Math.sqrt(Math.max(0, (E + disc) / 2));
            smin = Math.sqrt(Math.max(0, (E - disc) / 2));
        }
        const stretch = Math.max(smax / scale, scale / Math.max(smin, 1e-9));
        const ar = faceArea[t];
        stretchWeighted += stretch * ar;
        stretchAreaSum += ar;
        if (stretch > stretchMax) stretchMax = stretch;
    }

    // Seam stats: shared edges whose two triangles carry different labels.
    let seamLen = 0, seamEdges = 0;
    for (const e of edgeMap.values()) {
        if (e.tris.length !== 2 || label[e.tris[0]] === label[e.tris[1]]) continue;
        seamLen += e.len;
        seamEdges++;
    }

    return {
        uv,
        stretchStats: { avg: stretchAreaSum ? stretchWeighted / stretchAreaSum : 1, max: stretchMax },
        seamStats: { edges: seamEdges, length: seamLen },
    };
}
